import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Voicemail, Clock, User, Building, Search, Filter, RefreshCw, MessageSquare, Bot, ChevronRight, ExternalLink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interactionTypeIcons = {
  inbound_call: PhoneIncoming,
  outbound_call: PhoneOutgoing,
  missed_call: PhoneMissed,
  voicemail: Voicemail,
  sms_inbound: MessageSquare,
  sms_outbound: MessageSquare,
  callback: Phone
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  missed: 'bg-red-100 text-red-800',
  voicemail: 'bg-purple-100 text-purple-800',
  transferred: 'bg-cyan-100 text-cyan-800',
  abandoned: 'bg-gray-100 text-gray-800'
};

export default function CallCenterBoard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [stats, setStats] = useState({ total: 0, today: 0, missed: 0, aiHandled: 0 });

  const fetchInteractions = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('interaction_type', typeFilter);
      params.append('page_size', '100');
      
      const response = await axios.get(`${API}/call-center/interactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setInteractions(response.data.interactions || []);
      
      // Calculate stats
      const all = response.data.interactions || [];
      const today = new Date().toISOString().split('T')[0];
      setStats({
        total: response.data.total || 0,
        today: all.filter(i => i.created_at?.startsWith(today)).length,
        missed: all.filter(i => i.status === 'missed').length,
        aiHandled: all.filter(i => i.handled_by_ai).length
      });
    } catch (err) {
      console.error('Failed to fetch interactions:', err);
      toast.error('Failed to load interactions');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    fetchInteractions();
    const interval = setInterval(fetchInteractions, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchInteractions]);

  const filteredInteractions = interactions.filter(i => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      i.caller_name?.toLowerCase().includes(search) ||
      i.caller_number?.includes(search) ||
      i.contact_name?.toLowerCase().includes(search)
    );
  });

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString();
  };

  return (
    <Layout>
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Call Center Board</h1>
          <p className="text-muted-foreground">Manage and track all customer interactions</p>
        </div>
        <Button onClick={fetchInteractions} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Interactions</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Phone className="h-8 w-8 text-primary opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Today</p>
                <p className="text-2xl font-bold">{stats.today}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Missed Calls</p>
                <p className="text-2xl font-bold text-red-600">{stats.missed}</p>
              </div>
              <PhoneMissed className="h-8 w-8 text-red-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">AI Handled</p>
                <p className="text-2xl font-bold text-purple-600">{stats.aiHandled}</p>
              </div>
              <Bot className="h-8 w-8 text-purple-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="missed">Missed</SelectItem>
                <SelectItem value="voicemail">Voicemail</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="inbound_call">Inbound Call</SelectItem>
                <SelectItem value="outbound_call">Outbound Call</SelectItem>
                <SelectItem value="missed_call">Missed Call</SelectItem>
                <SelectItem value="voicemail">Voicemail</SelectItem>
                <SelectItem value="sms_inbound">SMS Inbound</SelectItem>
                <SelectItem value="sms_outbound">SMS Outbound</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Interactions List */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Interactions</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : filteredInteractions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No interactions found</div>
          ) : (
            <div className="space-y-2">
              {filteredInteractions.map((interaction) => {
                const Icon = interactionTypeIcons[interaction.interaction_type] || Phone;
                return (
                  <div
                    key={interaction.id}
                    onClick={() => setSelectedInteraction(interaction)}
                    className="flex items-center gap-4 p-4 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                  >
                    <div className={`p-2 rounded-full ${interaction.direction === 'inbound' ? 'bg-green-100' : 'bg-blue-100'}`}>
                      <Icon className={`h-5 w-5 ${interaction.direction === 'inbound' ? 'text-green-600' : 'text-blue-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">
                          {interaction.caller_name || interaction.contact_name || 'Unknown'}
                        </span>
                        {interaction.handled_by_ai && (
                          <Badge variant="secondary" className="text-xs">
                            <Bot className="h-3 w-3 mr-1" /> AI
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{interaction.caller_number}</span>
                        {interaction.queue_name && (
                          <>
                            <span>•</span>
                            <span>{interaction.queue_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={statusColors[interaction.status] || 'bg-gray-100'}>
                        {interaction.status}
                      </Badge>
                      <div className="text-xs text-muted-foreground mt-1">
                        {formatTime(interaction.created_at)}
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <div className="font-medium">{formatDuration(interaction.duration_seconds)}</div>
                      {interaction.disposition && (
                        <div className="text-xs text-muted-foreground">{interaction.disposition}</div>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Interaction Detail Modal */}
      <Dialog open={!!selectedInteraction} onOpenChange={() => setSelectedInteraction(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Interaction Details</DialogTitle>
          </DialogHeader>
          {selectedInteraction && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">Caller</label>
                  <p className="font-medium">{selectedInteraction.caller_name || 'Unknown'}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Phone</label>
                  <p className="font-medium">{selectedInteraction.caller_number}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Type</label>
                  <p className="font-medium capitalize">{selectedInteraction.interaction_type?.replace(/_/g, ' ')}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Status</label>
                  <Badge className={statusColors[selectedInteraction.status]}>
                    {selectedInteraction.status}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Duration</label>
                  <p className="font-medium">{formatDuration(selectedInteraction.duration_seconds)}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Queue</label>
                  <p className="font-medium">{selectedInteraction.queue_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Source</label>
                  <p className="font-medium">{selectedInteraction.source || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Campaign</label>
                  <p className="font-medium">{selectedInteraction.campaign || 'N/A'}</p>
                </div>
                {selectedInteraction.disposition && (
                  <div className="col-span-2">
                    <label className="text-sm text-muted-foreground">Disposition</label>
                    <p className="font-medium capitalize">{selectedInteraction.disposition.replace(/_/g, ' ')}</p>
                  </div>
                )}
                {selectedInteraction.recording_url && (
                  <div className="col-span-2">
                    <label className="text-sm text-muted-foreground">Recording</label>
                    <audio controls className="w-full mt-1">
                      <source src={selectedInteraction.recording_url} />
                    </audio>
                  </div>
                )}
                {selectedInteraction.transcript && (
                  <div className="col-span-2">
                    <label className="text-sm text-muted-foreground">Transcript</label>
                    <p className="text-sm bg-muted p-3 rounded mt-1">{selectedInteraction.transcript}</p>
                  </div>
                )}
              </div>
              {selectedInteraction.lead_id && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate(`/workspace/records?source=callcenter&mode=leads&id=${selectedInteraction.lead_id}`)}
                >
                  <User className="h-4 w-4 mr-2" />
                  View Lead
                  <ExternalLink className="h-3 w-3 ml-auto" />
                </Button>
              )}
              {selectedInteraction.contact_id && !selectedInteraction.lead_id && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate(`/workspace/records?source=callcenter&mode=leads&id=${selectedInteraction.contact_id}`)}
                >
                  <User className="h-4 w-4 mr-2" />
                  View Lead
                  <ExternalLink className="h-3 w-3 ml-auto" />
                </Button>
              )}
              {selectedInteraction.job_id && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate(`/workspace/records?source=callcenter&mode=projects&id=${selectedInteraction.job_id}`)}
                >
                  <Building className="h-4 w-4 mr-2" />
                  View Project
                  <ExternalLink className="h-3 w-3 ml-auto" />
                </Button>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedInteraction(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
